// BRIEF 117 §5 — TIME IS SET IN FIVE-MINUTE STEPS, ON A REAL CLOCK FACE.
//
// One control, mounted at every site the census found, replacing
// `<input type="time">`. The VALUE CONTRACT IS UNCHANGED and that is the point:
// in and out it is the same `"HH:mm"` string the native input handed over, so
// nothing downstream — `form.time`, `workHours.start`, the Graph builders, the
// alert engine's Brussels wall-clock comparison — sees any difference.
//
// ── §5a: THE MINUTE SIDE OFFERS FIVES ───────────────────────────────────────
//
// `step` is in MINUTES and defaults to 5, so the column is 00/05/…/55. The
// schedule axis passes 60 because it is a whole-hour axis and throws minutes
// away already (`ScheduleAxisPanel.tsx`, `Number(v.slice(0, 2))`); giving it a
// five-minute column would have offered twelve values eleven of which are
// discarded on arrival.
//
// A STORED ODD VALUE RENDERS AS IS AND SNAPS ON THE NEXT EDIT. `09:07` shows as
// `09:07` and its column highlights the nearest rung; touching either column
// writes a value on the grid. Nothing rewrites a row that is not being edited —
// §5a is explicit, and it is the difference between a picker and a migration.
//
// ── §5b: THE FACE, AND THE THIRD SELECTOR THAT IS NOT HERE ──────────────────
//
// Analog face + hours + minutes, in house tokens, no vendor widget. The
// TIMEZONE selector of Laurent's reference is deliberately absent, and §5b
// requires the measurement rather than the omission:
//
//   `AgendaEvent` has `date DateTime?` and `time String?` and NO zone column
//   (schema.prisma:953-956). The zone is not stored anywhere — it is ASSUMED,
//   as `Europe/Brussels`, and it is assumed in five places that would all have
//   to agree with a per-event value: the alert engine's wall-clock now
//   (alertEngine.js:192) and its 08:00 reminder hour (:173), and the Graph
//   builders' start/end and their `Prefer` header (m365.js:1179-1180, 1190-1191,
//   1351-1352, 1392).
//
// So a zone selector would be a control writing to a field that does not exist,
// over four hardcoded readers. §5b's own instruction covers exactly this case:
// render the face and two columns, and REGISTER the timezone half as a
// successor with the measurement. It is registered in the PR body.
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { CSSProperties } from "react";
import { useTranslation } from "../i18n";
import { RAD, FS, Z } from "../lib/tokens";
import { useMenuLevel } from "../lib/paneStacking"; // 204 §1
import { useAnchoredMenu } from "../hooks/useAnchoredMenu"; // 207B §7 — the one anchoring

/** `"HH:mm"` → minutes since midnight, or null for anything else. */
export function hmToMinutes(v: unknown): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(v ?? ""));
  if (!m) return null;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  if (h > 23 || mi > 59) return null;
  return h * 60 + mi;
}
const pad = (n: number) => String(n).padStart(2, "0");
const HOURS = Array.from({ length: 24 }, (_, i) => i);

/** The rung a stored value sits on or nearest to — highlight only; §5a forbids
 *  rewriting it until the user edits. */
function nearestRung(minute: number, step: number) {
  return (Math.round(minute / step) * step) % 60;
}

const ROW: CSSProperties = {
  padding: "3px 10px",
  fontSize: FS.s12,
  fontFamily: "'JetBrains Mono',monospace",
  cursor: "pointer",
  borderRadius: RAD.r4,
  textAlign: "center",
  color: "var(--ts)",
  lineHeight: "18px",
};

function Face({ minutes }: { minutes: number | null }) {
  // 12-hour face: the hand angles are what a clock shows, not what the value is.
  const h = minutes == null ? null : (minutes / 60) % 12;
  const m = minutes == null ? null : minutes % 60;
  const hAng = h == null ? 0 : h * 30 - 90;
  const mAng = m == null ? 0 : m * 6 - 90;
  const R = 46;
  const pt = (deg: number, len: number) => [
    56 + Math.cos((deg * Math.PI) / 180) * len,
    56 + Math.sin((deg * Math.PI) / 180) * len,
  ];
  const [hx, hy] = pt(hAng, 24);
  const [mx, my] = pt(mAng, 36);
  return (
    <svg width={112} height={112} aria-hidden="true" style={{ flexShrink: 0 }}>
      <circle cx={56} cy={56} r={R} fill="var(--bi)" stroke="var(--br)" strokeWidth={1} />
      {HOURS.slice(0, 12).map((i) => {
        const a = i * 30 - 90;
        const [x1, y1] = pt(a, R - 6);
        const [x2, y2] = pt(a, R - (i % 3 === 0 ? 12 : 9));
        return (
          <line
            key={i}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="var(--tm)"
            strokeWidth={i % 3 === 0 ? 2 : 1}
          />
        );
      })}
      {minutes != null && (
        <>
          <line x1={56} y1={56} x2={hx} y2={hy} stroke="var(--tp)" strokeWidth={3} strokeLinecap="round" />
          <line x1={56} y1={56} x2={mx} y2={my} stroke="var(--ac)" strokeWidth={2} strokeLinecap="round" />
        </>
      )}
      <circle cx={56} cy={56} r={3} fill="var(--ac)" />
    </svg>
  );
}

function Column({
  values,
  selected,
  fmt,
  onPick,
  label,
}: {
  values: number[];
  selected: number | null;
  fmt: (v: number) => string;
  onPick: (v: number) => void;
  label: string;
}) {
  const box = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const el = box.current?.querySelector<HTMLElement>("[data-sel='1']");
    if (el && box.current) box.current.scrollTop = el.offsetTop - 40;
  }, [selected]);
  return (
    <div style={{ display: "flex", flexDirection: "column", minWidth: 46 }}>
      <div
        style={{
          fontSize: FS.s9,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: 0.4,
          color: "var(--tm)",
          textAlign: "center",
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div ref={box} style={{ maxHeight: 112, overflowY: "auto" }}>
        {values.map((v) => {
          const on = v === selected;
          return (
            <div
              key={v}
              data-sel={on ? "1" : undefined}
              onClick={() => onPick(v)}
              style={{
                ...ROW,
                ...(on
                  ? { background: "var(--ac)", color: "var(--bf)", fontWeight: 700 }
                  : null),
              }}
            >
              {fmt(v)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function TimeField({
  value,
  onChange,
  step = 5,
  disabled,
  className = "field-input",
  style,
  placeholder,
  ...rest
}: {
  /** `"HH:mm"` or `""`. Exactly what `<input type="time">` carried. */
  value?: string | null;
  onChange: (v: string) => void;
  /** Minute granularity. 5 everywhere but the whole-hour schedule axis. */
  step?: number;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  placeholder?: string;
  [k: string]: unknown;
}) {
  const { t } = useTranslation();
  /* 207B §7 — THE PANEL SITS ON ITS FIELD: it anchors through
     `useAnchoredMenu`, as MenuSelect does — measured in a layout effect, the
     zoom corrected, flipped above when below cannot hold it, re-placed on a
     scroll, closed on a resize. It took a `getBoundingClientRect()` SNAPSHOT
     on click into state and wrote those numbers as `position: fixed`
     coordinates, which under the app's root zoom land in the zoomed space
     (the hook's own comment says why): MEASURED off its field at every text
     size but medium. What the panel needed that the hook lacked — its OWN
     width, wider than the field — widened the hook (`ownWidth`). */
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  /* 204 §1 — the portal pair (999 / 1000), or one over a ModalLayout LIFTED
     over an open pane. */
  const menuZ = useMenuLevel(Z.portal);
  const mins = hmToMinutes(value);
  const hour = mins == null ? null : Math.floor(mins / 60);
  const minute = mins == null ? null : mins % 60;
  const rungs =
    step >= 60 ? [0] : Array.from({ length: Math.floor(60 / step) }, (_, i) => i * step);
  /* §5a — the SELECTED rung for highlighting only. A stored 09:07 highlights 05
     and still reads 09:07 in the trigger until something is picked. */
  const selRung = minute == null ? null : nearestRung(minute, step >= 60 ? 60 : step);

  /* Registered ONCE and closing over nothing: `setOpen(o => o ? false : o)` asks
     the current state instead of depending on it. An effect that lists `open`
     and calls `setOpen` re-enters itself, and with a cleanup that is brief 78's
     fatal shape — `scripts/lint/effect-sets-its-own-dep.js` refuses it, and
     refused this file's first version. */
  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen((o) => (o ? false : o));
    };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, []);

  const commit = (h: number, m: number) => onChange(`${pad(h)}:${pad(m % 60)}`);
  const pickHour = (h: number) => commit(h, selRung ?? 0);
  const pickMinute = (m: number) => {
    commit(hour ?? 0, m);
    setOpen(false);
  };
  useAnchoredMenu({ open, anchorRef, menuRef, onDismiss: () => { setOpen(false); }, ownWidth: true });

  return (
    <>
      <button
        {...rest}
        ref={anchorRef}
        type="button"
        className={className}
        disabled={disabled}
        style={{
          textAlign: "left",
          fontFamily: "'JetBrains Mono',monospace",
          cursor: disabled ? undefined : "pointer",
          ...style,
        }}
        onClick={() => { setOpen((o) => !o); }}
      >
        {value || (
          <span style={{ color: "var(--tm)" }}>{placeholder || "--:--"}</span>
        )}
      </button>
      {open &&
        createPortal(
          <>
            {/* The dismiss backdrop, CLAUDE.md's portal pattern: a menu that
                escapes its container also escapes its container's click
                handling, so the way out is drawn rather than inherited. */}
            <div
              style={{ position: "fixed", inset: 0, zIndex: menuZ - 1 }}
              onClick={() => { setOpen(false); }}
            />
            <div
              ref={menuRef}
              style={{
                position: "fixed",
                /* 207B §7 — placed by `useAnchoredMenu` (left, top, and shown
                   once measured): below the field, flipped above when below
                   cannot hold it (114 §2's rule, on the panel's measured
                   height), clamped to the window by the panel's own width. */
                left: 0,
                top: 0,
                visibility: "hidden",
                /* The same step `Combobox` takes (:334) and for the same
                   reason: these fields sit inside modals, and
                   `.modal-overlay` is z-index 100 (App.css:940). */
                zIndex: menuZ,
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
                background: "var(--bc)",
                border: "1px solid var(--br)",
                borderRadius: RAD.r8,
                boxShadow: "0 8px 28px rgba(0,0,0,.3)",
                padding: 10,
              }}
            >
              <Face minutes={mins} />
              <Column
                values={HOURS}
                selected={hour}
                fmt={pad}
                onPick={pickHour}
                label={t("time.col_hours", { fallback: "Hours" })}
              />
              <Column
                values={rungs}
                selected={selRung}
                fmt={pad}
                onPick={pickMinute}
                label={t("time.col_minutes", { fallback: "Min" })}
              />
              <button
                type="button"
                className="link-btn"
                style={{
                  fontSize: FS.s10,
                  color: "var(--tm)",
                  cursor: "pointer",
                  background: "none",
                  border: "none",
                  alignSelf: "flex-end",
                }}
                onClick={() => {
                  onChange("");
                  setOpen(false);
                }}
              >
                {t("common.clear", { fallback: "Clear" })}
              </button>
            </div>
          </>,
          document.body,
        )}
    </>
  );
}
